import { useEffect, useState } from 'react';
import type { VoiceMode } from '@/protocol/frames';
import { useSession } from '@/session/store';

/**
 * Reports this client's screen state to Andromeda whenever the voice mode changes — the one part
 * of `screenState` that actually varies here. This client is a single voice console rather than a
 * real STB with multiple screens, so `screen`/`description` are fixed placeholders; this is the
 * point a real screen model would hook into if this client ever tracked more than one.
 *
 * Mirrors the store's `voiceMode` into local state so the effect only fires on an actual change —
 * `voiceMode` starts at the client's own default and moves only when Andromeda redirects it with
 * `changeVoiceState`, which the store folds into that same field.
 */
export function useScreenState(): VoiceMode {
  const voiceMode = useSession((state) => state.voiceMode);
  const [reported, setReported] = useState(voiceMode);

  useEffect(() => {
    if (voiceMode === reported) return;
    setReported(voiceMode);
    useSession.getState().sendFrame({
      type: 'screenState',
      voiceMode,
      screen: 'voiceConsole',
      description: {},
    });
  }, [voiceMode, reported]);

  return reported;
}
